import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonPage,
  IonSearchbar,
  IonSpinner,
  IonText,
  IonIcon,
  useIonToast,
} from "@ionic/react";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import Header from "src/components/Header";
import ServiciosEspecializadosAPI, {
  ProductoOportunidad,
  ServicioEspecializado,
} from "src/services/serviciosEspecializados";
import "./ServiciosEspecializados.css";
import { chevronDownOutline } from "ionicons/icons";

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
};

const formatDate = (value?: string | null, withTime?: boolean) => {
  if (!value) return "-";
  const parsed = moment(value);
  if (!parsed.isValid()) return formatValue(value);
  return parsed.format(withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");
};

const formatEstado = (estado?: string | null) => {
  const codigo = (estado || "").toUpperCase().trim();
  switch (codigo) {
    case "FIN":
      return "Finalizado";
    case "PGR":
      return "En progreso";
    case "PEN":
      return "Pendiente";
    default:
      return formatValue(estado);
  }
};

const estadoClass = (estado?: string | null) => {
  const codigo = (estado || "").toUpperCase().trim();
  switch (codigo) {
    case "FIN":
      return "se-estado se-estado--fin";
    case "PGR":
      return "se-estado se-estado--pgr";
    case "PEN":
      return "se-estado se-estado--pen";
    default:
      return "se-estado";
  }
};

const PAGE_SIZE = 20;

const ServiciosEspecializados: React.FC = () => {
  const [datos, setDatos] = useState<ServicioEspecializado[]>([]);
  const [productosMap, setProductosMap] = useState<Record<number, ProductoOportunidad[]>>({});
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [productsExpandedId, setProductsExpandedId] = useState<number | null>(
    null
  );
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(0);
  const [present] = useIonToast();
  const history = useHistory();

  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      try {
        const [resp, productosResp] = await Promise.all([
          ServiciosEspecializadosAPI.listar(),
          ServiciosEspecializadosAPI.listarProductos(),
        ]);
        setDatos(resp?.datos || []);
        const mapa: Record<number, ProductoOportunidad[]> = {};
        (productosResp?.datos || []).forEach((prod) => {
          const id = Number(prod.OportunidadCab);
          if (Number.isNaN(id)) return;
          if (!mapa[id]) mapa[id] = [];
          mapa[id].push(prod);
        });
        setProductosMap(mapa);
      } catch (error) {
        setDatos([]);
        setProductosMap({});
        present({
          message: "No se pudo cargar Servicios Especializados.",
          duration: 2500,
          color: "danger",
        });
        console.error("Servicios Especializados:", error);
      } finally {
        setLoading(false);
      }
    };

    cargar();
  }, [present]);

  // Ordena de mas reciente a mas antiguo (por fecha de insercion, con Id como respaldo).
  const datosOrdenados = useMemo(() => {
    const clave = (item: ServicioEspecializado) => {
      const fecha = moment(item.FechaInsercion);
      return fecha.isValid() ? fecha.valueOf() : 0;
    };
    return [...datos].sort((a, b) => {
      const diff = clave(b) - clave(a);
      if (diff !== 0) return diff;
      return (Number(b.Id) || 0) - (Number(a.Id) || 0);
    });
  }, [datos]);

  const datosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) return datosOrdenados;
    return datosOrdenados.filter((item) => {
      const ps = String(item.PuntoServicio ?? "").toLowerCase();
      const nro = String(item.NroPresupuesto ?? "").toLowerCase();
      return ps.includes(termino) || nro.includes(termino);
    });
  }, [datosOrdenados, busqueda]);

  const totalPaginas = Math.max(1, Math.ceil(datosFiltrados.length / PAGE_SIZE));
  const paginaActual = Math.min(page, totalPaginas - 1);
  const datosPagina = useMemo(
    () =>
      datosFiltrados.slice(
        paginaActual * PAGE_SIZE,
        paginaActual * PAGE_SIZE + PAGE_SIZE
      ),
    [datosFiltrados, paginaActual]
  );

  // Vuelve a la primera pagina cuando cambia la busqueda.
  useEffect(() => {
    setPage(0);
  }, [busqueda]);

  return (
    <IonPage>
      <Header titulo="Servicios Especializados" />
      <IonContent id="servicios-especializados">
        {loading ? (
          <div className="se-center">
            <IonSpinner />
          </div>
        ) : (
          <>
            <div className="se-search">
              <IonSearchbar
                placeholder="Buscar por Punto de Servicio o Nro. Presupuesto"
                value={busqueda}
                debounce={200}
                onIonChange={(ev) => setBusqueda(ev.detail.value ?? "")}
              />
            </div>
            {datosFiltrados.length === 0 ? (
              <div className="se-center">
                <IonText>
                  {busqueda.trim()
                    ? "No hay resultados para tu busqueda."
                    : "No hay servicios especializados para mostrar."}
                </IonText>
              </div>
            ) : (
          <div className="se-list">
            {datosPagina.map((item) => {
              const isExpanded = expandedId === item.Id;
              const productsExpanded = productsExpandedId === item.Id;
              return (
              <IonCard key={`${item.Id}-${item.NroPresupuesto}`}>
                <IonCardHeader
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setExpandedId(isExpanded ? null : item.Id);
                    setProductsExpandedId(null);
                  }}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter") {
                      setExpandedId(isExpanded ? null : item.Id);
                      setProductsExpandedId(null);
                    }
                  }}
                >
                  <IonCardTitle>{item.NroPresupuesto}</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className="se-preview">
                    <div>
                      <span className="se-label">Tipo presupuesto:</span>
                      <strong>{formatValue(item.TipoPresupuesto)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Mes cobertura:</span>
                      <strong>{formatDate(item.MesCobertura)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Punto servicio:</span>
                      <strong>{formatValue(item.PuntoServicio)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Cuenta:</span>
                      <strong>{formatValue(item.Cuenta)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Cant. horas:</span>
                      <strong>{formatValue(item.CantHoras)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Estado:</span>
                      <strong className={estadoClass(item.Estado)}>
                        {formatEstado(item.Estado)}
                      </strong>
                    </div>
                  </div>

                  <div className="se-actions">
                    <IonButton
                      size="small"
                      fill="outline"
                      onClick={() => history.push(`/servicios-especializados/${item.Id}`)}
                    >
                      Ver detalles y Asignacion
                    </IonButton>
                  </div>

                  {isExpanded && (
                    <div className="se-detail">
                    <div className="se-grid">
                    <div>
                      <span className="se-label">Contacto:</span>
                      <strong>{formatValue(item.Contacto)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Tipo presupuesto:</span>
                      <strong>{formatValue(item.TipoPresupuesto)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Mes cobertura:</span>
                      <strong>{formatDate(item.MesCobertura)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Unidad negocio:</span>
                      <strong>{formatValue(item.UnidadNegocio)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Punto servicio:</span>
                      <strong>{formatValue(item.PuntoServicio)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Nombre fantasia:</span>
                      <strong>{formatValue(item.NombreFantasia)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Rubro:</span>
                      <strong>{formatValue(item.Rubro)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Ejecutivo venta:</span>
                      <strong>{formatValue(item.EjecutivoVenta)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Observacion:</span>
                      <strong>{formatValue(item.Observacion)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Fecha inicio:</span>
                      <strong>{formatDate(item.FechaInicio)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Total m2:</span>
                      <strong>{formatValue(item.TotalMetros2)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Cant. horas:</span>
                      <strong>{formatValue(item.CantHoras)}</strong>
                    </div>
                    <div>
                      <span className="se-label">Cuenta:</span>
                      <strong>{formatValue(item.Cuenta)}</strong>
                    </div>
                  </div>
                  <div className="se-products">
                    <div className="se-products-header">
                      <div className="se-products-title">Productos</div>
                      <IonButton
                        size="small"
                        fill="clear"
                        onClick={() =>
                          setProductsExpandedId(
                            productsExpanded ? null : item.Id
                          )
                        }
                        aria-label="Mostrar productos"
                      >
                        <IonIcon icon={chevronDownOutline} />
                      </IonButton>
                    </div>
                    {productsExpanded && (
                      (productosMap[item.Id] || []).length === 0 ? (
                        <IonText color="medium">Sin productos asociados.</IonText>
                      ) : (
                        <div className="se-products-list">
                          {(productosMap[item.Id] || []).map((prod, idx) => (
                            <div key={`${item.Id}-${idx}`} className="se-product">
                              <div className="se-product-name">
                                {formatValue(prod.NombreProducto)}
                              </div>
                              <div className="se-product-desc">
                                {formatValue(prod.Descripcion)}
                              </div>
                              <div className="se-product-qty">
                                Cantidad: {formatValue(prod.Cantidad)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                  </div>
                  )}
                </IonCardContent>
              </IonCard>
            )})}
          </div>
            )}
            {datosFiltrados.length > PAGE_SIZE && (
              <div className="se-pagination">
                <IonButton
                  size="small"
                  fill="outline"
                  disabled={paginaActual === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Anterior
                </IonButton>
                <IonText className="se-pagination-info">
                  Pagina {paginaActual + 1} de {totalPaginas}
                </IonText>
                <IonButton
                  size="small"
                  fill="outline"
                  disabled={paginaActual >= totalPaginas - 1}
                  onClick={() =>
                    setPage((p) => Math.min(totalPaginas - 1, p + 1))
                  }
                >
                  Siguiente
                </IonButton>
              </div>
            )}
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default ServiciosEspecializados;
